import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Alert,
  AlertIcon,
  Link,
  Stack
} from '@chakra-ui/react';
import ForgotPasswordModal from './ForgotPasswordModal';

const LoginModal = ({ show, onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const { login, loading, error, success } = useContext(AuthContext);

  // Check if we're redirected from password reset
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'success') {
      setResetSuccess(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await login(username, password);
  };

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  }, [success, onClose]);

  return (
    <>
      <Modal isOpen={show} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.600" />
        <ModalContent
          bg="linear-gradient(135deg, rgba(10, 10, 31, 0.95) 0%, rgba(26, 10, 46, 0.95) 100%)"
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.200"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
        >
          <ModalHeader
            fontSize="2xl"
            fontWeight="bold"
            bgGradient="linear(to-r, #667eea, #764ba2)"
            bgClip="text"
          >
            Log In
          </ModalHeader>
          <ModalCloseButton color="whiteAlpha.800" _hover={{ bg: 'whiteAlpha.200' }} />
          <ModalBody pb={6}>
            <Box as="form" onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {error && (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert status="success">
                    <AlertIcon />
                    Login successful!
                  </Alert>
                )}
                {resetSuccess && (
                  <Alert status="success">
                    <AlertIcon />
                    Password reset successful! Please log in with your new password.
                  </Alert>
                )}
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" color="whiteAlpha.900">Username</FormLabel>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    isDisabled={loading}
                    placeholder="Enter your username"
                    size="md"
                    color="white"
                    bg="whiteAlpha.100"
                    borderColor="purple.500"
                    _placeholder={{ color: "whiteAlpha.500" }}
                    _hover={{ bg: "whiteAlpha.150", borderColor: "purple.400" }}
                    _focus={{ bg: "whiteAlpha.150", borderColor: "purple.300", boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.6)" }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" color="whiteAlpha.900">Password</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    isDisabled={loading}
                    placeholder="Enter your password"
                    size="md"
                    color="white"
                    bg="whiteAlpha.100"
                    borderColor="purple.500"
                    _placeholder={{ color: "whiteAlpha.500" }}
                    _hover={{ bg: "whiteAlpha.150", borderColor: "purple.400" }}
                    _focus={{ bg: "whiteAlpha.150", borderColor: "purple.300", boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.6)" }}
                  />
                </FormControl>
                <Button
                  mt={6}
                  bgGradient="linear(to-r, #667eea, #764ba2)"
                  _hover={{
                    bgGradient: "linear(to-r, #764ba2, #667eea)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.6)"
                  }}
                  type="submit"
                  isLoading={loading}
                  loadingText="Logging in"
                  isFullWidth
                  size="md"
                  fontWeight="bold"
                  color="white"
                >
                  Log In
                </Button>
                <Box textAlign="center" mt={4}>
                  <Link
                    color="blue.300"
                    onClick={() => setShowForgotPassword(true)}
                    cursor="pointer"
                    fontWeight="medium"
                    fontSize="sm"
                    _hover={{ textDecoration: 'underline', color: 'blue.200' }}
                  >
                    Forgot Password?
                  </Link>
                </Box>
              </Stack>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      <ForgotPasswordModal
        show={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
};

export default LoginModal;
